import _ from "lodash";

import { OrderWithOrderVariants } from "@models/Order";
import { OrderSet } from "@models/OrderSet";
import { OrderSetVariant } from "@models/OrderSetVariant";

export type ExtendedOrderSetVariant = OrderSetVariant & {
  price?: string;
  totalQty?: number;
  totalPrice?: number;
};
// insures both orderVariants and orderSetVariants are sorted by the same key
const variantSortKeys = [
  "variant.item.name", // names are unique
  "variant.orderPosition", // respect order position for variants
  "variant.variantSku", // if order-position is not unique, it will fall back to this
];

// Declare the combine function outside of the hook
// so that its reference is stable and doesn't cause re-renders
export function combineOrderSetAndOrders(
  orderSet: OrderSet | undefined,
  orders: OrderWithOrderVariants[] | undefined
) {
  if (!orderSet || !orders) {
    return null;
  }
  const sortedOrdersData = _(orders)
    .map((order) => ({
      ...order,
      orderVariants: _.sortBy(order.orderVariants, ...variantSortKeys),
    }))
    .sortBy("address.state.code", "address.region", "address.id")
    .value();

  const lookup = _(orders)
    .flatMap((o) => o.orderVariants)
    .groupBy("variant.id")
    .mapValues((v) =>
      v.reduce(
        (a, b) => {
          a.price = b.price;
          a.qty += b.qty;
          return a;
        },
        { price: "0", qty: 0 }
      )
    )
    .value();

  const orderSetVariants: ExtendedOrderSetVariant[] = _(
    orderSet.orderSetVariants
  )
    .sortBy(...variantSortKeys)
    .map((osv, i) => ({
      ...osv,
      price: lookup[osv.variant.id]?.price ?? null,
      totalQty: lookup[osv.variant.id]?.qty ?? 0,
      totalPrice: sortedOrdersData.reduce(
        (totalPrice, order) =>
          totalPrice + +(order.orderVariants[i]?.totalPrice || 0),
        0
      ),
    }))
    .value();
  return {
    orderSet: orderSet,
    orders: sortedOrdersData,
    orderSetVariants,
  };
}
